import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const ChatbotSidebar: React.FC<{
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}> = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    {
      label: 'Analytics',
      path: '/chatbot-admin/analytics',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      description: 'Métricas e relatórios de uso'
    },
    {
      label: 'Chatbots',
      path: '/chatbot-admin/workspaces',
      icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      description: 'Gerenciar chatbots e workspaces'
    },
    {
      label: 'Assistente Virtual',
      path: '/chatbot-admin/virtual-assistant',
      icon: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      description: 'Teste e demonstre o chatbot'
    },
    {
      label: 'Documentos',
      path: '/chatbot-admin/documents',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      description: 'Documentos indexados para chatbots'
    },
    {
      label: 'Usuários',
      path: '/chatbot-admin/users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      description: 'Controle de acesso e permissões'
    },
    {
      label: 'Provedores',
      path: '/chatbot-admin/providers',
      icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
      description: 'Configurar provedores de LLM, Embedding e Vector DB'
    }
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-label="Fechar menu"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`bg-white shadow-md flex flex-col flex-shrink-0 z-50 h-full min-h-0 fixed inset-y-0 left-0 w-64 transition-transform duration-200 md:static md:z-40 ${
          collapsed ? 'md:w-20' : 'md:w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none md:pointer-events-auto md:translate-x-0'}`}
      >
      <div
        className={`border-b border-gray-200 flex items-center justify-between p-4 ${
          collapsed ? 'md:flex-col md:items-center md:gap-1 md:px-2 md:py-3' : ''
        }`}
      >
        <img src="/logo.png" alt="Logo" className="h-8" />
        <button
          className="md:hidden flex items-center justify-center p-2 rounded hover:bg-blue-50 focus:outline-none"
          onClick={onMobileClose}
          aria-label="Fechar menu"
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <button
          className="hidden md:flex items-center justify-center p-2 rounded hover:bg-blue-50 focus:outline-none"
          onClick={() => {
            setCollapsed(c => !c);
          }}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
        </button>
      </div>

      <div className="md:hidden px-3 py-3 border-b border-gray-200 space-y-1">
        <Link
          to="/"
          onClick={onMobileClose}
          className="block rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Voltar para Aplicação
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto">
        <ul>
          {menuItems.map(item => (
            <li key={item.path} className="mb-2">
              <Link
                to={item.path}
                onClick={onMobileClose}
                className={`flex items-center px-4 py-2 rounded-md transition-colors duration-150 ${
                  isActive(item.path)
                    ? 'bg-blue-900 text-white'
                    : 'text-blue-900 hover:bg-blue-50'
                } ${collapsed ? 'md:justify-center' : ''}`}
                tabIndex={0}
                title={collapsed ? item.label : undefined}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 mr-3 ${collapsed ? 'md:mr-0' : ''} ${
                    isActive(item.path) ? 'text-white' : 'text-blue-900'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={item.icon}
                  />
                </svg>
                <div className={`flex flex-col ${collapsed ? 'md:hidden' : ''}`}>
                  <span className="font-medium">{item.label}</span>
                  <span className={`text-xs ${
                    isActive(item.path) ? 'text-white' : 'text-gray-500'
                  }`}>{item.description}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      </aside>
    </>
  );
};
